/**
 * quizAnswerKey — keep TEST answer keys off the student-readable class doc.
 *
 * A class doc is readable by every class member (students included), so any
 * answer-revealing field on a test question (correctIndex, model answers, numeric
 * keys, the graph-input target, etc.) is visible to a student who inspects the
 * loaded data. This module splits a quiz into:
 *   - a SANITIZED copy (answer fields blanked) → stored on the class doc, safe to
 *     hand to students; enough to DISPLAY every question.
 *   - an ANSWER KEY (the stripped fields, per question + per variant) → stored in
 *     the owner-only private doc (classes/{id}/private/…), read by the teacher UI
 *     and the server grader.
 *
 * Matching questions: the correct pairing is encoded in the pair ORDER
 * (matchPairs[i].left ↔ matchPairs[i].right), so we can't just blank a field. On
 * the class doc we SCRAMBLE the right-hand values across pairs (each left is shown
 * beside a wrong right) and record the permutation privately as `matchScramble`.
 * The student sees every right (safe to display) but not which left it belongs to.
 * `hydrateQuestions` UN-scrambles back to the aligned original for the teacher and
 * server (and re-attaches the permutation so a later save reuses it), so every
 * teacher-facing path sees the original pairs unchanged; the server converts the
 * student's scrambled-index answer via the permutation at grading time.
 *
 * `prepareQuizForStorage` is the single storage-boundary entry point: it produces
 * BOTH the sanitized class-doc questions AND the private key in one pass (matching
 * needs a shared permutation, so extract + sanitize can't run independently for it).
 */
import type { PopQuizQuestion, PopQuizVariant, PopQuiz, PopQuizMatchPair } from '../types';

/** The answer-revealing fields lifted off a question/variant. For matching, the
 *  answer is the pairing, stored as the scramble permutation (`matchScramble`). */
export interface AnswerFields {
  correctIndex?: number;
  correctIndices?: number[];
  modelAnswer?: string;
  answerValue?: string;
  acceptedForms?: string[];
  equivalenceMode?: 'equivalent' | 'exact';
  tolerance?: number;
  tolerancePercent?: number;
  /** graph-input target (the answer); the rest of graphInput stays for display. */
  graphAnswer?: { targetPoints?: { x: number; y: number }[]; targetLine?: { slope: number; intercept: number } };
  /** matching: the permutation used to scramble the class-doc rights (private). */
  matchScramble?: number[];
}

/** Per-quiz answer key stored in the private doc. */
export interface QuizAnswerKey {
  /** questionId -> its answer fields */
  questions: Record<string, AnswerFields>;
  /** questionId -> variantId -> that variant's answer fields */
  variants: Record<string, Record<string, AnswerFields>>;
  /** test-level AI grading guidance for written answers (also teacher-private) */
  aiGradingInstructions?: string;
}

const isNum = (v: unknown): v is number => typeof v === 'number' && !Number.isNaN(v);

/** A pseudo-random source in [0,1). Defaults to Math.random; tests inject a seed. */
export type Rng = () => number;

/** A derangement of [0..n-1] (a permutation with NO fixed point) via Sattolo's
 *  algorithm, which yields a single n-cycle — so for n>=2 no index maps to itself
 *  and no left is ever shown beside its own correct right. n<2 can't be hidden
 *  (a 0/1-pair match is degenerate), so it returns the identity. */
function derangement(n: number, rng: Rng): number[] {
  const a = Array.from({ length: n }, (_, i) => i);
  for (let i = n - 1; i > 0; i--) {
    const j = Math.floor(rng() * i); // strictly < i → Sattolo (single cycle, no fixed points)
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/** Inverse permutation: inv[perm[i]] === i. */
function invertPerm(perm: number[]): number[] {
  const inv = new Array<number>(perm.length);
  for (let i = 0; i < perm.length; i++) inv[perm[i]] = i;
  return inv;
}

/** A true bijection of [0..n-1]: length n, every entry a distinct integer in range.
 *  (A plain invert+every check passes sparse garbage like [2,2,0] because Array#every
 *  skips holes — so validate the values explicitly to reject a corrupted key.) */
const isValidPerm = (p: unknown, n: number): p is number[] => {
  if (!Array.isArray(p) || p.length !== n) return false;
  const seen = new Set<number>();
  for (const v of p) {
    if (!Number.isInteger(v) || v < 0 || v >= n || seen.has(v)) return false;
    seen.add(v);
  }
  return true;
};

/** Scramble a matching question's rights across pairs so no left is shown with its
 *  correct right. `scrambled[i].right = aligned[perm[i]].right` (lefts stay put).
 *  Returns the scrambled pairs + the permutation to store privately. Reuses an
 *  existing valid permutation (stable across re-saves; in-flight students keep the
 *  order they loaded); otherwise derives a fresh derangement. */
function scrambleRights(aligned: PopQuizMatchPair[], existing: number[] | undefined, rng: Rng): { scrambled: PopQuizMatchPair[]; perm: number[] } {
  const n = aligned.length;
  const perm = isValidPerm(existing, n) ? existing : derangement(n, rng);
  const scrambled = aligned.map((p, i) => ({ ...p, right: aligned[perm[i]].right }));
  return { scrambled, perm };
}

/** Invert scrambleRights: given the class-doc scrambled pairs + the permutation,
 *  rebuild the aligned original. `aligned[j].right = scrambled[inv[j]].right`. */
function unscrambleRights(scrambled: PopQuizMatchPair[], perm: number[]): PopQuizMatchPair[] {
  if (!isValidPerm(perm, scrambled.length)) return scrambled;
  const inv = invertPerm(perm);
  return scrambled.map((p, j) => ({ ...p, right: scrambled[inv[j]].right }));
}

/** Pull the answer fields out of a question OR variant. Only defined fields are
 *  included, so the stored key stays compact and round-trips cleanly. */
function pluckAnswerFields(q: PopQuizQuestion | PopQuizVariant): AnswerFields {
  const out: AnswerFields = {};
  if (isNum((q as PopQuizQuestion).correctIndex)) out.correctIndex = (q as PopQuizQuestion).correctIndex;
  if (Array.isArray(q.correctIndices)) out.correctIndices = q.correctIndices;
  if (typeof q.modelAnswer === 'string') out.modelAnswer = q.modelAnswer;
  if (typeof q.answerValue === 'string') out.answerValue = q.answerValue;
  if (Array.isArray(q.acceptedForms)) out.acceptedForms = q.acceptedForms;
  if (q.equivalenceMode) out.equivalenceMode = q.equivalenceMode;
  if (isNum(q.tolerance)) out.tolerance = q.tolerance;
  if (isNum(q.tolerancePercent)) out.tolerancePercent = q.tolerancePercent;
  const gi = (q as PopQuizQuestion).graphInput;
  if (gi && (gi.targetPoints || gi.targetLine)) {
    out.graphAnswer = {
      ...(gi.targetPoints ? { targetPoints: gi.targetPoints } : {}),
      ...(gi.targetLine ? { targetLine: gi.targetLine } : {}),
    };
  }
  if (Array.isArray((q as PopQuizQuestion).matchScramble)) out.matchScramble = (q as PopQuizQuestion).matchScramble;
  return out;
}

/** Blank a question's/variant's answer fields for the student-facing copy. Keeps the
 *  field SHAPE valid (correctIndex is required on the type, so it becomes -1 = "no
 *  answer") while removing anything that reveals the answer. Never mutates the input. */
function blankAnswerFields<T extends PopQuizQuestion | PopQuizVariant>(q: T): T {
  const {
    correctIndices: _ci, modelAnswer: _ma, answerValue: _av, acceptedForms: _af,
    equivalenceMode: _em, tolerance: _t, tolerancePercent: _tp, ...rest
  } = q as PopQuizQuestion;
  const sanitized = { ...(rest as unknown as T), correctIndex: -1 };
  // Strip only the answer key out of graphInput; keep the display spec.
  const gi = (q as PopQuizQuestion).graphInput;
  if (gi) {
    const { targetPoints: _gp, targetLine: _gl, ...giRest } = gi;
    (sanitized as unknown as PopQuizQuestion).graphInput = giRest;
  }
  return sanitized;
}

/** Collect the whole answer key for a quiz (questions + their variants + the
 *  test-level AI grading guidance). */
export function extractAnswerKey(quiz: Pick<PopQuiz, 'questions' | 'aiGradingInstructions'>): QuizAnswerKey {
  const key: QuizAnswerKey = { questions: {}, variants: {} };
  for (const q of quiz.questions || []) {
    key.questions[q.id] = pluckAnswerFields(q);
    if (Array.isArray(q.variants) && q.variants.length) {
      const vmap: Record<string, AnswerFields> = {};
      for (const v of q.variants) vmap[v.id] = pluckAnswerFields(v);
      key.variants[q.id] = vmap;
    }
  }
  if (typeof quiz.aiGradingInstructions === 'string' && quiz.aiGradingInstructions.trim()) {
    key.aiGradingInstructions = quiz.aiGradingInstructions.trim();
  }
  return key;
}

/** Non-matching answer content on a question OR variant (matching is handled by the
 *  parent-level scramble flag, not here). */
function fieldsHaveContent(q: PopQuizQuestion | PopQuizVariant): boolean {
  const gi = (q as PopQuizQuestion).graphInput;
  return (isNum((q as PopQuizQuestion).correctIndex) && (q as PopQuizQuestion).correctIndex >= 0)
    || (Array.isArray(q.correctIndices) && q.correctIndices.length > 0)
    || (typeof q.modelAnswer === 'string' && q.modelAnswer.length > 0)
    || (typeof q.answerValue === 'string' && q.answerValue.length > 0)
    || (Array.isArray(q.acceptedForms) && q.acceptedForms.length > 0)
    || !!(gi && (gi.targetPoints || gi.targetLine));
}

/** Does this question still carry any answer-revealing field? Used to tell a REAL
 *  (unstripped) test from an already-sanitized one — a sanitized question has
 *  correctIndex -1 and every other answer field removed, so this returns false.
 *  Keying the strip decision on this (not correctIndex alone) is important: an
 *  AI/Word-imported all-written or all-matching test can legitimately carry
 *  correctIndex -1 while still holding model answers / numeric keys to strip. */
export function hasAnswerContent(q: PopQuizQuestion): boolean {
  if (!q) return false;
  // Matching: the answer is the pairing. Aligned pairs (fresh, or hydrated back for
  // the teacher) ARE the answer and must be scrambled/stripped; once scrambled on the
  // class doc (matchScrambled) they're safe. The parent flag governs its variants too.
  if (Array.isArray(q.matchPairs) && q.matchPairs.length > 0 && !q.matchScrambled) return true;
  if (fieldsHaveContent(q)) return true;
  // Variant-only content: a parent could be answerless while a variant still carries a key.
  return Array.isArray(q.variants) && q.variants.some(fieldsHaveContent);
}

/** Produce the student-facing questions with every answer field blanked (variants
 *  too). NOTE: this does NOT scramble matching pairs — use `prepareQuizForStorage`
 *  for the storage boundary. Kept for the non-matching-sensitive callers/tests. */
export function sanitizeQuestions(questions: PopQuizQuestion[]): PopQuizQuestion[] {
  return (questions || []).map((q) => {
    const sq = blankAnswerFields(q);
    if (Array.isArray(q.variants) && q.variants.length) {
      sq.variants = q.variants.map((v) => blankAnswerFields(v));
    }
    return sq;
  });
}

/** Single storage-boundary entry point: split a quiz into the SANITIZED class-doc
 *  questions + the private ANSWER KEY in ONE pass. Matching questions are scrambled
 *  here (extract + sanitize can't run independently for them — they share a
 *  permutation), so this is what updateClass/createClass must call. Never mutates
 *  the input; the class-doc copy carries no answer (matching rights scrambled + a
 *  `matchScrambled` marker, the permutation stored only in the returned key). */
export function prepareQuizForStorage(
  quiz: Pick<PopQuiz, 'questions' | 'aiGradingInstructions'>,
  rng: Rng = Math.random,
): { sanitized: PopQuizQuestion[]; key: QuizAnswerKey } {
  const key: QuizAnswerKey = { questions: {}, variants: {} };

  const prepMatch = <T extends PopQuizQuestion | PopQuizVariant>(src: T, blanked: T, kf: AnswerFields): T => {
    const pairs = src.matchPairs;
    if (!Array.isArray(pairs) || pairs.length === 0) return blanked;
    const { scrambled, perm } = scrambleRights(pairs, src.matchScramble, rng);
    kf.matchScramble = perm;
    const out = { ...(blanked as PopQuizQuestion), matchPairs: scrambled } as PopQuizQuestion;
    // The permutation is PRIVATE — never let it ride along on the class-doc copy.
    delete out.matchScramble;
    return out as unknown as T;
  };

  const sanitized = (quiz.questions || []).map((q) => {
    const kf = pluckAnswerFields(q);
    let sq = blankAnswerFields(q);
    if (q.type === 'match') {
      sq = prepMatch(q, sq, kf);
      // Mark the class-doc copy as scrambled so it's never re-scrambled on a raw re-write.
      if (Array.isArray(q.matchPairs) && q.matchPairs.length > 0) sq.matchScrambled = true;
    }
    key.questions[q.id] = kf;
    if (Array.isArray(q.variants) && q.variants.length) {
      const vmap: Record<string, AnswerFields> = {};
      sq.variants = q.variants.map((v) => {
        const vkf = pluckAnswerFields(v);
        const sv = q.type === 'match' ? prepMatch(v, blankAnswerFields(v), vkf) : blankAnswerFields(v);
        vmap[v.id] = vkf;
        return sv;
      });
      key.variants[q.id] = vmap;
    }
    return sq;
  });

  if (typeof quiz.aiGradingInstructions === 'string' && quiz.aiGradingInstructions.trim()) {
    key.aiGradingInstructions = quiz.aiGradingInstructions.trim();
  }
  return { sanitized, key };
}

/** Merge an answer key back onto sanitized questions — the inverse of
 *  sanitize+extract — so the teacher UI and server grader see the full quiz.
 *  A question/variant with no stored key is returned unchanged. */
export function hydrateQuestions(questions: PopQuizQuestion[], key: QuizAnswerKey | null | undefined): PopQuizQuestion[] {
  if (!key) return questions;
  const applyTo = <T extends PopQuizQuestion | PopQuizVariant>(q: T, fields: AnswerFields | undefined): T => {
    if (!fields) return q;
    const merged = { ...q } as PopQuizQuestion;
    if (fields.correctIndex !== undefined) merged.correctIndex = fields.correctIndex;
    if (fields.correctIndices !== undefined) merged.correctIndices = fields.correctIndices;
    if (fields.modelAnswer !== undefined) merged.modelAnswer = fields.modelAnswer;
    if (fields.answerValue !== undefined) merged.answerValue = fields.answerValue;
    if (fields.acceptedForms !== undefined) merged.acceptedForms = fields.acceptedForms;
    if (fields.equivalenceMode !== undefined) merged.equivalenceMode = fields.equivalenceMode;
    if (fields.tolerance !== undefined) merged.tolerance = fields.tolerance;
    if (fields.tolerancePercent !== undefined) merged.tolerancePercent = fields.tolerancePercent;
    if (fields.graphAnswer && merged.graphInput) {
      merged.graphInput = { ...merged.graphInput, ...fields.graphAnswer };
    }
    // Matching: un-scramble the class-doc rights back to the aligned original so the
    // teacher/server see the real pairing, and re-attach the permutation so the
    // server can convert the student's scrambled-index answer (and a later save
    // reuses the same order). The class-doc `matchScrambled` marker drops (now aligned).
    if (Array.isArray(merged.matchPairs) && merged.matchPairs.length
        && isValidPerm(fields.matchScramble, merged.matchPairs.length)) {
      merged.matchPairs = unscrambleRights(merged.matchPairs, fields.matchScramble);
      merged.matchScramble = fields.matchScramble;
      delete merged.matchScrambled;
    }
    return merged as unknown as T;
  };
  return (questions || []).map((q) => {
    const hq = applyTo(q, key.questions[q.id]);
    const vkey = key.variants[q.id];
    if (vkey && Array.isArray(hq.variants) && hq.variants.length) {
      hq.variants = hq.variants.map((v) => applyTo(v, vkey[v.id]));
    }
    return hq;
  });
}
